import type { Provider } from '@angular/core';

import {
	activateUserBuilder,
	add2FAMethodBuilder,
	changePasswordBuilder,
	checkUserStatusBuilder,
	delete2FAMethodBuilder,
	findAllPasskeysByUserIdBuilder,
	generateApiKeyBuilder,
	generateEmailOtpSecretBuilder,
	generateTotpSecretBuilder,
	getUserTwoFactorMethodsBuilder,
	loginBuilder,
	logoutBuilder,
	recoveryPasswordBuilder,
	refreshSessionBuilder,
	resetPasswordBuilder,
	revertSuplantBuilder,
	revertUserActivationBuilder,
	signupBuilder,
	suplantUserBuilder,
	suplantUserByDeviceIdentificatorBuilder,
	twoFAloginBuilder,
	unlockUserBuilder,
	update2FAMethodBuilder,
	verifyEmailOtpCodeBuilder,
	verifyTotpCodeBuilder
} from '@contexts/auth/application';
import { deletePasskeyBuilder } from '@contexts/auth/application/use-cases/delete-passkey.usecase';
import type {
	AuthRepository,
	EmailOtpRepository,
	PasskeyRepository,
	TotpRepository
} from '@contexts/auth/domain';
import { repositoryFactoryBuilder } from '@contexts/shared/infrastructure';

import {
	ACTIVATE_USER_USE_CASE,
	ADD_2FA_METHOD_USE_CASE,
	CHANGE_PASSWORD_USE_CASE,
	CHECK_USER_STATUS_USE_CASE,
	DELETE_2FA_METHOD_USE_CASE,
	DELETE_PASSKEY_USE_CASE,
	FIND_ALL_PASSKEYS_BY_USER_ID_USE_CASE,
	GENERATE_API_KEY_USE_CASE,
	GENERATE_EMAIL_OTP_SECRET_USE_CASE,
	GENERATE_TOTP_SECRET_USE_CASE,
	GET_USER_TWO_FACTOR_METHODS_USE_CASE,
	LOGIN_USE_CASE,
	LOGOUT_USE_CASE,
	RECOVERY_PASSWORD_USE_CASE,
	REFRESH_SESSION_USE_CASE,
	RESET_PASSWORD_USE_CASE,
	REVERT_SUPLANT_USE_CASE,
	REVERT_USER_ACTIVATION_USE_CASE,
	SIGNUP_USE_CASE,
	SUPLANT_USER_BY_DEVICE_IDENTIFICATOR_USE_CASE,
	SUPLANT_USER_USE_CASE,
	TWO_FA_LOGIN_USE_CASE,
	UNLOCK_USER_USE_CASE,
	UPDATE_2FA_METHOD_USE_CASE,
	VERIFY_EMAIL_OTP_CODE_USE_CASE,
	VERIFY_TOTP_CODE_USE_CASE
} from '@dependencies/auth/application/use-cases';
import {
	AUTH_REPOSITORY,
	EMAIL_OTP_REPOSITORY,
	PASSKEY_REPOSITORY,
	TOTP_REPOSITORY
} from '@dependencies/auth/domain/contracts';
import {
	AUTH_REPOSITORY_DEFINITION,
	EMAIL_OTP_REPOSITORY_DEFINITION,
	PASSKEY_REPOSITORY_DEFINITION,
	TOTP_REPOSITORY_DEFINITION
} from '@dependencies/auth/infrastructure';

export const authDependencies: Provider[] = [
	repositoryFactoryBuilder<AuthRepository>('auth', AUTH_REPOSITORY, AUTH_REPOSITORY_DEFINITION),
	{
		provide: LOGIN_USE_CASE,
		useFactory: loginBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: LOGOUT_USE_CASE,
		useFactory: logoutBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: SIGNUP_USE_CASE,
		useFactory: signupBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: TWO_FA_LOGIN_USE_CASE,
		useFactory: twoFAloginBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: ACTIVATE_USER_USE_CASE,
		useFactory: activateUserBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: REVERT_USER_ACTIVATION_USE_CASE,
		useFactory: revertUserActivationBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: CHECK_USER_STATUS_USE_CASE,
		useFactory: checkUserStatusBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: UPDATE_2FA_METHOD_USE_CASE,
		useFactory: update2FAMethodBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: DELETE_2FA_METHOD_USE_CASE,
		useFactory: delete2FAMethodBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: ADD_2FA_METHOD_USE_CASE,
		useFactory: add2FAMethodBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: GET_USER_TWO_FACTOR_METHODS_USE_CASE,
		useFactory: getUserTwoFactorMethodsBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: CHANGE_PASSWORD_USE_CASE,
		useFactory: changePasswordBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: RECOVERY_PASSWORD_USE_CASE,
		useFactory: recoveryPasswordBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: RESET_PASSWORD_USE_CASE,
		useFactory: resetPasswordBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: REFRESH_SESSION_USE_CASE,
		useFactory: refreshSessionBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: UNLOCK_USER_USE_CASE,
		useFactory: unlockUserBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: SUPLANT_USER_USE_CASE,
		useFactory: suplantUserBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: SUPLANT_USER_BY_DEVICE_IDENTIFICATOR_USE_CASE,
		useFactory: suplantUserByDeviceIdentificatorBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: REVERT_SUPLANT_USE_CASE,
		useFactory: revertSuplantBuilder,
		deps: [AUTH_REPOSITORY]
	},
	{
		provide: GENERATE_API_KEY_USE_CASE,
		useFactory: generateApiKeyBuilder,
		deps: [AUTH_REPOSITORY]
	}
];

export const totpDependencies: Provider[] = [
	repositoryFactoryBuilder<TotpRepository>('totp', TOTP_REPOSITORY, TOTP_REPOSITORY_DEFINITION),
	{
		provide: GENERATE_TOTP_SECRET_USE_CASE,
		useFactory: generateTotpSecretBuilder,
		deps: [TOTP_REPOSITORY]
	},
	{
		provide: VERIFY_TOTP_CODE_USE_CASE,
		useFactory: verifyTotpCodeBuilder,
		deps: [TOTP_REPOSITORY]
	}
];

export const emailOtpDependencies: Provider[] = [
	repositoryFactoryBuilder<EmailOtpRepository>(
		'email-otp',
		EMAIL_OTP_REPOSITORY,
		EMAIL_OTP_REPOSITORY_DEFINITION
	),
	{
		provide: GENERATE_EMAIL_OTP_SECRET_USE_CASE,
		useFactory: generateEmailOtpSecretBuilder,
		deps: [EMAIL_OTP_REPOSITORY]
	},
	{
		provide: VERIFY_EMAIL_OTP_CODE_USE_CASE,
		useFactory: verifyEmailOtpCodeBuilder,
		deps: [EMAIL_OTP_REPOSITORY]
	}
];

export const passkeyDependencies: Provider[] = [
	repositoryFactoryBuilder<PasskeyRepository>(
		'passkey',
		PASSKEY_REPOSITORY,
		PASSKEY_REPOSITORY_DEFINITION
	),
	{
		provide: FIND_ALL_PASSKEYS_BY_USER_ID_USE_CASE,
		useFactory: findAllPasskeysByUserIdBuilder,
		deps: [PASSKEY_REPOSITORY]
	},
	{
		provide: DELETE_PASSKEY_USE_CASE,
		useFactory: deletePasskeyBuilder,
		deps: [PASSKEY_REPOSITORY]
	}
];

export * from '@dependencies/auth/application/use-cases';
export * from '@dependencies/auth/domain/contracts';
